// Session LIFECYCLE (ADR-014 §10): exactly four states, mirroring
// radar-api/src/utils/feedbackSessionStatus.js. Do NOT add a fifth value here —
// "closed early" is a property of the terminal state, not a state of its own
// (see FEEDBACK_SESSION_STATUS_FILTERS below and ADR-014 Decision 10).
export const FEEDBACK_SESSION_STATUSES = {
  NOT_STARTED: 'NotStarted',
  CONFIGURATION: 'Configuration',
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed'
};

// ADR-014: closure discriminator — mirrors the API's FeedbackSession.closureType.
// A Completed session with CLOSED_WITH_INCOMPLETE was closed early and may
// contain incomplete work frozen exactly as it was at the moment of closure.
export const FEEDBACK_SESSION_CLOSURE_TYPES = {
  ALL_COMPLETED: 'AllCompleted',
  CLOSED_WITH_INCOMPLETE: 'ClosedWithIncomplete'
};

// ADR-014 §10: the DISPLAY / FILTER vocabulary — the four lifecycle states with
// the terminal one split by closure type. This is what status chips render and
// what the status filter row offers; it is deliberately a superset of the
// lifecycle enum so that no behavioural code accidentally branches on it.
// Mirrors FEEDBACK_SESSION_STATUS_FILTERS in radar-api.
export const FEEDBACK_SESSION_STATUS_FILTERS = {
  ...FEEDBACK_SESSION_STATUSES,
  CLOSED_WITH_INCOMPLETE: FEEDBACK_SESSION_CLOSURE_TYPES.CLOSED_WITH_INCOMPLETE
};

// Null-safe: surfaces that also render individual assignments pass a null
// session (EmployeeHeader, the results cards), and a throw here would take the
// whole page down. Callers still gate on `isIndividualAssignment` themselves.
export const getSessionStatus = (session) => {
  if (session?.completedAt) {
    return FEEDBACK_SESSION_STATUSES.COMPLETED;
  }
  if (session?.startedAt) {
    return FEEDBACK_SESSION_STATUSES.IN_PROGRESS;
  }
  if (session?.publishedAt) {
    return FEEDBACK_SESSION_STATUSES.CONFIGURATION;
  }
  return FEEDBACK_SESSION_STATUSES.NOT_STARTED;
};

/**
 * ADR-014: was this session terminated with work still unfinished?
 *
 * Keyed on closureType alone: the API only ever writes CLOSED_WITH_INCOMPLETE
 * together with completedAt (closeWithIncomplete), and several read payloads
 * (e.g. the session report) carry closureType without selecting completedAt.
 * getSessionStatusFilterValue below still requires the terminal lifecycle
 * state before it will show a terminal chip, so a stray value cannot mislabel
 * a live session.
 */
export const isSessionClosedWithIncomplete = (session) => {
  return session?.closureType === FEEDBACK_SESSION_CLOSURE_TYPES.CLOSED_WITH_INCOMPLETE;
};

/**
 * ADR-014 §10: map a session onto the display/filter vocabulary. Identical to
 * getSessionStatus except that a terminal session which was closed early
 * resolves to CLOSED_WITH_INCOMPLETE instead of COMPLETED.
 *
 * A payload that does not carry closureType degrades to COMPLETED — the same
 * chip that shipped before ADR-014, never an unknown status.
 */
export const getSessionStatusFilterValue = (session) => {
  const status = getSessionStatus(session);
  if (status === FEEDBACK_SESSION_STATUSES.COMPLETED && isSessionClosedWithIncomplete(session)) {
    return FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE;
  }
  return status;
};

export const getStatusColor = (status) => {
  switch (status) {
    case FEEDBACK_SESSION_STATUSES.NOT_STARTED:
      return '#FEF3C7'; // Light amber background
    case FEEDBACK_SESSION_STATUSES.CONFIGURATION:
      return '#EEF2FF'; // Light indigo background
    case FEEDBACK_SESSION_STATUSES.IN_PROGRESS:
      return '#EBF5FF'; // Light blue background
    case FEEDBACK_SESSION_STATUSES.COMPLETED:
      return '#E2F8E9'; // Light green background
    case FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE:
      return '#F1F5F9'; // Light slate background — frozen/inactive (ADR-014)
    default:
      return '#F1F5F9'; // Light slate background
  }
};

export const getStatusText = (status) => {
  switch (status) {
    case FEEDBACK_SESSION_STATUSES.NOT_STARTED:
      return 'Not Started';
    case FEEDBACK_SESSION_STATUSES.CONFIGURATION:
      return 'Configuration';
    case FEEDBACK_SESSION_STATUSES.IN_PROGRESS:
      return 'In Progress';
    case FEEDBACK_SESSION_STATUSES.COMPLETED:
      return 'Completed';
    case FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE:
      // ADR-014 §10d: one word, so the chip fits the same envelope as its four
      // peers. "Closed" contrasts with "Completed" (done vs merely over); the
      // slate palette and the tooltip carry "work was left unfinished". Do not
      // use "Incomplete" alone — it reads as still-actionable.
      return 'Closed';
    default:
      return 'Unknown Status';
  }
};

export const getStatusBorderColor = (status) => {
  switch (status) {
    case FEEDBACK_SESSION_STATUSES.NOT_STARTED:
      return '#D97706'; // Amber border
    case FEEDBACK_SESSION_STATUSES.CONFIGURATION:
      return '#6366F1'; // Indigo border
    case FEEDBACK_SESSION_STATUSES.IN_PROGRESS:
      return '#2E90FA'; // Blue border
    case FEEDBACK_SESSION_STATUSES.COMPLETED:
      return '#16A34A'; // Green border
    case FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE:
      return '#94A3B8'; // Slate border — frozen/inactive (ADR-014)
    default:
      return '#475569'; // Slate border
  }
};

export const getStatusTextColor = (status) => {
  switch (status) {
    case FEEDBACK_SESSION_STATUSES.NOT_STARTED:
      return '#D97706'; // Amber text
    case FEEDBACK_SESSION_STATUSES.CONFIGURATION:
      return '#6366F1'; // Indigo text
    case FEEDBACK_SESSION_STATUSES.IN_PROGRESS:
      return '#2E90FA'; // Blue text
    case FEEDBACK_SESSION_STATUSES.COMPLETED:
      return '#16A34A'; // Green text
    case FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE:
      return '#64748B'; // Slate text — frozen/inactive (ADR-014)
    default:
      return '#475569'; // Slate text
  }
};

/**
 * ADR-014 §10i: what each status actually means, for the chip and filter-chip
 * tooltips. Every value in the display vocabulary has one — a chip that
 * explains itself on hover only *sometimes* teaches users that chips are not
 * worth hovering, which would bury the closure detail that matters most.
 *
 * The wording is derived from the lifecycle actions the app already describes
 * (`SessionCardItem` publish/start/complete/close-early button tooltips) so the
 * state and the transition into it say the same thing. Copy follows the
 * Individual / Reviewer UI naming convention.
 *
 * Each description states the CONDITION and what can be DONE in it — never a
 * restatement of the label ("In Progress: the session is in progress").
 */
export const getStatusDescription = (status) => {
  switch (status) {
    case FEEDBACK_SESSION_STATUSES.NOT_STARTED:
      return 'Created but not published. It is not available to anyone yet.';
    case FEEDBACK_SESSION_STATUSES.CONFIGURATION:
      return 'Published for configuration — individuals and reviewers are being set up. Not yet open to everyone.';
    case FEEDBACK_SESSION_STATUSES.IN_PROGRESS:
      return 'Open to all assigned individuals and reviewers. Assessments can be submitted.';
    case FEEDBACK_SESSION_STATUSES.COMPLETED:
      return 'Finalized with every assignment complete. The session is read-only.';
    case FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE:
      return 'Ended with unfinished work. Incomplete assignments were preserved as-is and can never be finished.';
    default:
      return '';
  }
};

/**
 * ADR-014 §10: the closed-early tooltip — the shared description plus the two
 * things only this session can say: how much was finished, and why it was
 * ended. Returns '' for anything that was not closed early.
 */
export const getSessionClosureTooltip = (session) => {
  if (!isSessionClosedWithIncomplete(session)) {
    return '';
  }
  const parts = [
    getStatusDescription(FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE)
  ];
  const summary = session?.closureSummary;
  if (summary && typeof summary.totalAssignments === 'number') {
    parts.push(`${summary.completedAssignments} of ${summary.totalAssignments} assignments were complete.`);
  }
  if (session?.closureReason) {
    parts.push(`Reason: ${session.closureReason}`);
  }
  return parts.join(' ');
};

/**
 * ADR-014 §10i: the tooltip for a rendered session chip.
 *
 * `value` overrides the derived state for payloads that carry `closureType`
 * but not the lifecycle timestamps (the session report header) — without it,
 * such a payload would derive `NotStarted` and show the wrong description.
 */
export const getSessionStatusTooltip = (session, value = null) => {
  const resolved = value || getSessionStatusFilterValue(session);
  if (resolved === FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE) {
    return getSessionClosureTooltip(session) || getStatusDescription(resolved);
  }
  return getStatusDescription(resolved);
};

/**
 * ADR-014 §10: the single source for rendering a session status chip. Returns
 * the resolved display vocabulary value plus its palette and tooltip, so a
 * caller renders ONE chip instead of a status chip plus a stacked closure tag.
 */
export const getSessionStatusDisplay = (session) => {
  const value = getSessionStatusFilterValue(session);
  return {
    value,
    label: getStatusText(value),
    backgroundColor: getStatusColor(value),
    textColor: getStatusTextColor(value),
    borderColor: getStatusBorderColor(value),
    isClosedEarly: value === FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE,
    tooltip: getSessionStatusTooltip(session)
  };
};
